import React, { useEffect, useRef, useState } from "react";
import {
  consumeOnboardingChatStream,
  getStarterKnowledgeStatus,
  type StarterKnowledgeStatus,
  type StarterStepStatus,
} from "./onboardingAgentStream";
import { getFreshIdToken } from "../../auth/getFreshIdToken";
import type { QuickStartValues } from "./QuickStartForm";
import {
  EMPTY_DETAILS,
  type BrandDetailsDraft,
  type CreatedBrand,
  type QuickStartResult,
} from "./quickStartOnboarding";
import { detailsFromPayload } from "./brandDetailsFromProfile";
import {
  applyStepEvent,
  finishAllSteps,
  type AgentStep,
} from "../../components/AIAgents/ChatWithAgent/components/AgentStepTimeline";
import {
  BrandDetailsCard,
  EditableProfileCard,
  LiveAssistantTurn,
  SetupComposer,
  SetupThread,
  type SetupOption,
  type ThreadBubble,
} from "./SetupChatParts";

/**
 * Setup chat (SGL-448), run by the Sagelyn AI Assistant end to end: it asks
 * about the website, reads it, asks for whatever is missing, applies the
 * owner's corrections and hears their "yes" (Sourav, 2026-10-17: no scripted
 * conversation).
 *
 * What stays in the app: the opening menu, the profile card with its two
 * buttons, the editable card, and creating the Brand. The model never creates
 * anything: the tested quickStartOnboarding does, after the owner confirms by
 * button or in words (output_business_profile `confirmed`).
 *
 * Two steps (SGL-512): confirming the profile card creates the Brand
 * (ONBOARDING) and saves what the website read found as a draft; the "Your
 * brand details" card then lets the owner check the logo, colours and voice,
 * and confirming it makes the Brand ACTIVE and creates the team.
 *
 * A failed or slow reply never falls back to a script: the owner sees what
 * happened, with Try again and Use the form instead.
 */

const REPLY_TIMEOUT_MS = 45000;

export const START_OPTIONS: SetupOption[] = [
  { key: "account", label: "Set up my account", description: "A few quick questions. No website needed.", icon: "rocket_launch" },
  { key: "support", label: "Create a support ticket", description: "Tell us the problem — we'll take it from there.", icon: "support_agent" },
  { key: "demo", label: "Show me a product demo", description: "Watch a quick tour of Sagelyn.", icon: "play_circle" },
];

interface Props {
  firstName: string;
  avatar: React.ReactNode;
  idToken: string;
  agentId: string;
  customerId: string;
  provider: string;
  /** Step 1: create the Brand and save what the website read found. */
  onCreateBrand: (values: QuickStartValues, found: BrandDetailsDraft) => Promise<CreatedBrand>;
  /** Step 2: save the confirmed details, create the team, start its knowledge. */
  onSaveDetails: (brand: CreatedBrand, values: QuickStartValues, details: BrandDetailsDraft) => Promise<QuickStartResult>;
  /** A Brand left after step 1: open straight on step 2 for it. */
  resume?: { brand: CreatedBrand; values: QuickStartValues };
  onUseForm: (values: Partial<QuickStartValues>) => void;
  onFinish: (result: QuickStartResult, destination: "agent" | "connect" | "details" | "edit", agentId?: string) => void;
  /** Tells the page the owner has sent a first message. */
  onStarted?: (started: boolean) => void;
  /** Opening-menu choices handled by the page (support ticket, tutorial). */
  onOtherAction?: (key: "support" | "demo") => void;
}

const EMPTY: QuickStartValues = { businessName: "", city: "", state: "", zip: "", country: "", website: "", services: [] };

const str = (v: unknown) => (typeof v === "string" ? v.trim() : "");
const list = (v: unknown) =>
  Array.isArray(v) ? v.map((x) => str(x)).filter(Boolean) : [];

/** Only non-empty fields, so a later turn never blanks an earlier answer. */
export function answersFromPayload(p: Record<string, unknown>): Partial<QuickStartValues> {
  const out: Partial<QuickStartValues> = {};
  if (str(p.business_name)) out.businessName = str(p.business_name);
  if (str(p.city)) out.city = str(p.city);
  if (str(p.state)) out.state = str(p.state);
  if (str(p.zip_code)) out.zip = str(p.zip_code);
  if (str(p.country)) out.country = str(p.country);
  if (str(p.website_url)) out.website = str(p.website_url);
  if (list(p.services).length) out.services = list(p.services).slice(0, 12);
  return out;
}

const isComplete = (v: Partial<QuickStartValues>) =>
  !!v.businessName?.trim() && !!v.city?.trim() && !!v.state?.trim();

/** What the model needs to know about a card the owner is looking at: the
 *  thread carries only text, so the card's fields ride along with it. */
const cardContext = (v: QuickStartValues) =>
  `\n\n[Profile card shown to the owner: business_name: ${v.businessName}; city: ${v.city}; ` +
  `state: ${v.state}; zip_code: ${v.zip || "(not given)"}; country: ${v.country || "(not given)"}; services: ${v.services.join(", ") || "(none)"}; ` +
  `website_url: ${v.website || "(none)"}]`;

type Phase =
  | "chat"
  | "creating" // step 1: the Brand
  | "details" // step 2: the owner checks the Brand details
  | "saving" // step 2 confirmed: details + team
  | "training"
  | "done"
  | "create_failed"
  | "save_failed";

/** How long the app watches training before showing the team anyway. The
 *  server keeps going; this only bounds how long the owner waits. */
const TRAINING_WATCH_MS = 5 * 60 * 1000;
const TRAINING_POLL_MS = 2500;

const toStepStatus = (s: StarterStepStatus): AgentStep["status"] =>
  s === "done" ? "done" : s === "error" ? "error" : "running";

/** Join names for a sentence: "Maya, Sam and Vera". */
const joinNames = (names: string[]) =>
  names.length <= 1 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

/** When the owner can talk to the assistant: not while a step runs or the
 *  Brand-details card is open (the card is how step 2 is answered). */
const CHAT_PHASES: Phase[] = ["chat", "done", "create_failed"];

const TEAM_LABELS = { running: "Saving your brand details and creating your team", done: "Saved your brand details and created your team" };

/** Step 2's opening line: what was found, and what to do with it. */
function detailsPrompt(firstName: string, v: QuickStartValues, b: CreatedBrand, returning: boolean): string {
  const opener = returning
    ? `Welcome back, ${firstName}. ${v.businessName} is set up; one step left: your brand details.`
    : `${v.businessName} is created. Next, your brand details.`;
  const found = b.details.logoUrl || b.details.colors.length || b.details.brandVoice;
  const ask = found
    ? " Here's what I found on your website. Change anything that's off, then save."
    : " Add your logo, colours and voice if you have them, or skip any of it; you can change them later in Brand details.";
  return opener + ask;
}

export const AssistantSetupChat: React.FC<Props> = (props) => {
  const { firstName, avatar, idToken, agentId, customerId, provider, onStarted, resume } = props;
  const [bubbles, setBubbles] = useState<ThreadBubble[]>(() =>
    resume
      ? [{ from: "assistant", text: detailsPrompt(firstName, resume.values, resume.brand, true) }]
      : [{ from: "assistant", text: `Hi ${firstName}, what would you like help with?`, options: START_OPTIONS }]
  );
  const [answers, setAnswers] = useState<Partial<QuickStartValues>>(resume?.values ?? {});
  // What the website read found for step 2, gathered across turns.
  // A ref, not state: the "yes" can arrive in the same turn that found them.
  const foundRef = useRef<Partial<BrandDetailsDraft>>({});
  // The Brand once step 1 has created it; never created twice.
  const [brand, setBrand] = useState<CreatedBrand | null>(resume?.brand ?? null);
  const creatingRef = useRef(false);
  // The details last confirmed, so a failed save can be retried as is.
  const lastDetails = useRef<BrandDetailsDraft | null>(null);
  const [busy, setBusy] = useState(false);
  const [streaming, setStreaming] = useState("");
  const [liveSteps, setLiveSteps] = useState<AgentStep[]>([]);
  const stepsRef = useRef<AgentStep[]>([]);
  // A reply that failed: the owner's message stays on screen and can be resent.
  const [turnError, setTurnError] = useState<{ message: string; text: string } | null>(null);
  const [editing, setEditing] = useState(false);
  const [phase, setPhase] = useState<Phase>(resume ? "details" : "chat");
  const [result, setResult] = useState<QuickStartResult | null>(null);
  // Setup's own steps (creating, reading the site, writing files, training),
  // shown live; the team appears only once training has finished.
  const [setupSteps, setSetupSteps] = useState<AgentStep[]>([]);
  const stepTimes = useRef<Record<string, { startedAt: number; endedAt?: number }>>({});
  // The finished setup steps, kept on the "Meet your team" message.
  const setupStepsRef = useRef<AgentStep[]>([]);
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const started = !!resume || bubbles.some((b) => b.from === "user");
  useEffect(() => {
    onStarted?.(started);
  }, [started, onStarted]);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ behavior: "smooth" });
    if (!busy) inputRef.current?.focus();
  }, [bubbles, streaming, busy, liveSteps, phase, editing, turnError]);

  const values = (): QuickStartValues => ({ ...EMPTY, ...answers });

  /** One setup step with its own wording; times are noted as it changes. */
  const setupStep = (
    id: string,
    status: AgentStep["status"],
    labels: NonNullable<AgentStep["labels"]>,
  ): AgentStep => {
    const now = Date.now();
    const t = (stepTimes.current[id] ??= { startedAt: now });
    if (status !== "running" && t.endedAt === undefined) t.endedAt = now;
    return { id, tool: id, status, labels, startedAt: t.startedAt, endedAt: t.endedAt };
  };

  const stepsFromStatus = (st: StarterKnowledgeStatus | null, names: string): AgentStep[] => {
    const out: AgentStep[] = [setupStep("team", "done", TEAM_LABELS)];
    if (st?.steps.website) {
      const pages = st.steps.website.pages ?? 0;
      out.push(setupStep("website", toStepStatus(st.steps.website.status), {
        running: "Reading your website",
        done: `Read ${pages} page${pages === 1 ? "" : "s"} of your website`,
        failed: "Couldn't read your website",
      }));
    }
    const files = st?.steps.files.status ?? "running";
    if (!st?.steps.website || st.steps.website.status !== "running") {
      out.push(setupStep("files", toStepStatus(files === "pending" ? "running" : files), {
        running: "Writing your ideal-customer profile, services, FAQ and pricing",
        done: "Wrote your ideal-customer profile, services, FAQ and pricing",
        failed: "Couldn't write your starter files",
      }));
    }
    const training = st?.steps.training.status;
    if (training && training !== "pending") {
      out.push(setupStep("training", toStepStatus(training), {
        running: `Training ${names}`,
        done: `Trained ${names}`,
        failed: `Some files didn't finish training`,
      }));
    }
    return out;
  };

  const showTeam = (r: QuickStartResult, v: QuickStartValues, note: string) => {
    const missing = r.failedAgents.length
      ? ` ${r.failedAgents.join(" and ")} couldn't be created just now; you can add ${r.failedAgents.length > 1 ? "them" : "it"} later from Agents.`
      : "";
    setBubbles((prev) => [
      ...prev,
      { from: "assistant", text: `${v.businessName} is set up. Meet your team.${missing}${note}`, team: r.agents, steps: setupStepsRef.current },
      {
        from: "assistant",
        text: "Want to tell me more about your business, like your services and customers? Your team does much better work when they know.",
      },
    ]);
    setSetupSteps([]);
    setPhase("done");
  };

  /** Watch the server's training until it finishes (or the watch window ends). */
  const watchTraining = async (r: QuickStartResult, v: QuickStartValues) => {
    const names = joinNames(r.agents.map((a) => a.firstName));
    const started = Date.now();
    let notStartedPolls = 0;
    while (Date.now() - started < TRAINING_WATCH_MS) {
      let st: StarterKnowledgeStatus | null = null;
      try {
        st = await getStarterKnowledgeStatus(await getFreshIdToken(), r.customerId);
      } catch (e) {
        console.error("Starter knowledge status check failed:", e);
      }
      if (st === null) notStartedPolls += 1;
      const steps = stepsFromStatus(st, names);
      setupStepsRef.current = steps;
      setSetupSteps(steps);
      if (st?.done) {
        const siteNote = st.steps.website?.status === "error"
          ? " I couldn't read your website, so their starter files use what you told me; add your website in Brand details any time."
          : "";
        showTeam(r, v, (st.ok ? "" : " Some of their starter files didn't finish; you can add them from each agent's edit page.") + siteNote);
        return;
      }
      if (notStartedPolls > 12) break; // ~30s with no job at all
      await new Promise((res) => setTimeout(res, TRAINING_POLL_MS));
    }
    showTeam(r, v, " They're still learning from your website and starter files; it can take another minute.");
  };

  /** Step 1: create the Brand, then show the Brand-details card. */
  const create = async (v: QuickStartValues) => {
    if (brand || creatingRef.current) return; // a later "yes" never makes a second Brand
    creatingRef.current = true;
    setEditing(false);
    stepTimes.current = {};
    const labels = { running: "Creating your Brand", done: "Created your Brand" };
    setSetupSteps([setupStep("brand", "running", labels)]);
    setPhase("creating");
    try {
      const created = await props.onCreateBrand(v, { ...EMPTY_DETAILS, ...foundRef.current });
      setBrand(created);
      setSetupSteps([]);
      setBubbles((prev) => [
        ...prev,
        { from: "assistant", text: detailsPrompt(firstName, v, created, false), steps: [setupStep("brand", "done", labels)] },
      ]);
      setPhase("details");
    } catch (e) {
      const reason = e instanceof Error && e.message ? ` (${e.message})` : "";
      setSetupSteps([]);
      setBubbles((prev) => [...prev, { from: "assistant", text: `I couldn't create your Brand${reason}.` }]);
      setPhase("create_failed");
    } finally {
      creatingRef.current = false;
    }
  };

  /** Step 2: save the Brand details (the Brand goes ACTIVE), then the team. */
  const confirmDetails = async (details: BrandDetailsDraft) => {
    if (!brand) return;
    lastDetails.current = details;
    const v = values();
    stepTimes.current = {};
    setBubbles((prev) => [...prev, { from: "assistant", text: "", details }]);
    setSetupSteps([setupStep("team", "running", TEAM_LABELS)]);
    setPhase("saving");
    try {
      const r = await props.onSaveDetails(brand, v, details);
      setResult(r);
      if (r.knowledgeStarted && r.agents.length > 0) {
        setPhase("training");
        await watchTraining(r, v);
      } else {
        setupStepsRef.current = [setupStep("team", "done", TEAM_LABELS)];
        showTeam(r, v, r.agents.length ? " Their starter files couldn't be prepared just now; you can add files from each agent's edit page." : "");
      }
    } catch (e) {
      console.error("[SGL-512] Saving Brand details failed:", e);
      const reason = e instanceof Error && e.message ? ` (${e.message})` : "";
      setSetupSteps([]);
      setBubbles((prev) => [...prev, { from: "assistant", text: `I couldn't save your brand details${reason}. Nothing is lost; try again.` }]);
      setPhase("save_failed");
    }
  };

  /** Send the owner's words to the assistant. `resend` retries a failed turn
   *  without adding the message to the thread twice. */
  const send = async (raw: string, resend = false) => {
    const text = raw.trim();
    if (!text || busy || !CHAT_PHASES.includes(phase)) return;
    const thread: ThreadBubble[] = resend ? bubbles : [...bubbles, { from: "user", text }];
    setBubbles(thread);
    setTurnError(null);
    setEditing(false);
    setBusy(true);
    setStreaming("");
    stepsRef.current = [];
    setLiveSteps([]);
    try {
      const timeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("The assistant took too long to reply.")), REPLY_TIMEOUT_MS)
      );
      const r = await Promise.race([
        consumeOnboardingChatStream({
          idToken,
          agentId,
          customerId,
          provider,
          messages: thread.map((b) => ({
            role: b.from,
            content: b.summary ? b.text + cardContext(b.summary) : b.text,
          })),
          setStreamStatus: () => undefined,
          setStreamingContent: setStreaming,
          onStep: (event) => {
            stepsRef.current = applyStepEvent(stepsRef.current, event);
            setLiveSteps(stepsRef.current);
          },
        }),
        timeout,
      ]);
      if (r.streamError) throw new Error(r.streamError.message || "The assistant hit an error.");

      const payload = r.mergedProfilePayload || {};
      const merged: QuickStartValues = { ...EMPTY, ...answers, ...answersFromPayload(payload) };
      setAnswers(merged);
      foundRef.current = { ...foundRef.current, ...detailsFromPayload(payload) };
      const reply = r.assistantText.trim() || str(payload.message);
      if (!reply) throw new Error("The assistant sent an empty reply.");

      // "Building your business profile" is real only when a card follows a
      // website read; after other answers it is bookkeeping, not news.
      const allSteps = finishAllSteps(stepsRef.current);
      const readSite = allSteps.some((s) => s.tool === "fetch_website_profile" && s.status === "done");
      const ready = isComplete(merged) && (payload.ready === true || readSite);
      const steps = ready && readSite ? allSteps : allSteps.filter((s) => s.tool !== "output_business_profile");

      if (payload.confirmed === true && isComplete(merged) && !brand) {
        setBubbles([...thread, { from: "assistant", text: reply, steps }]);
        await create(merged);
        return;
      }
      setBubbles([...thread, { from: "assistant", text: reply, steps, summary: ready ? merged : undefined }]);
    } catch (e) {
      // Loud for us, plain for the owner, and never a scripted stand-in.
      console.error("Onboarding assistant reply failed:", e);
      setTurnError({
        message: e instanceof Error && e.message ? e.message : "Something went wrong.",
        text,
      });
    } finally {
      setBusy(false);
      setStreaming("");
      stepsRef.current = [];
      setLiveSteps([]);
    }
  };

  const last = bubbles[bubbles.length - 1];
  const cardOpen = phase === "chat" && !busy && !turnError && last?.from === "assistant" && !!last.summary;

  const replies: { label: string; onClick: () => void; primary?: boolean }[] =
    cardOpen && !editing
      ? [
          { label: "Yes, that's right", primary: true, onClick: () => create(values()) },
          { label: "Change something", onClick: () => setEditing(true) },
        ]
      : phase === "done" && result
        ? [
            { label: "Add more details", primary: true, onClick: () => props.onFinish(result, "details") },
            { label: "Connect my accounts", onClick: () => props.onFinish(result, "connect") },
          ]
        : phase === "save_failed" && lastDetails.current
          ? [{ label: "Try again", primary: true, onClick: () => confirmDetails(lastDetails.current as BrandDetailsDraft) }]
        : phase === "create_failed"
          ? [
              { label: "Try again", primary: true, onClick: () => create(values()) },
              { label: "Use the form instead", onClick: () => props.onUseForm(values()) },
            ]
          : turnError
            ? [
                { label: "Try again", primary: true, onClick: () => send(turnError.text, true) },
                { label: "Use the form instead", onClick: () => props.onUseForm(values()) },
              ]
            : [];

  return (
    <div className="flex w-full flex-col gap-8 text-left" aria-label="Set up your business">
      <SetupThread
        bubbles={bubbles}
        avatar={avatar}
        onChatAgent={(id) => result && props.onFinish(result, "agent", id)}
        onEditAgent={(id) => result && props.onFinish(result, "edit", id)}
        onOption={(key) => {
          if (key === "account") send("Set up my account");
          else if (key === "support") send("Create a support ticket");
          else props.onOtherAction?.(key as "demo");
        }}
      />

      {busy && (
        <LiveAssistantTurn
          avatar={avatar}
          showHeader={last?.from !== "assistant"}
          steps={liveSteps.filter(
            (s) => s.tool !== "output_business_profile" || liveSteps.some((x) => x.tool === "fetch_website_profile")
          )}
          streaming={streaming}
        />
      )}

      {turnError && (
        <p className="pl-[54px] text-[14px] text-red-600" role="alert">
          {turnError.message} Your message wasn't lost.
        </p>
      )}

      {editing && cardOpen && (
        <div className="pl-[54px]">
          <EditableProfileCard
            values={values()}
            onCancel={() => setEditing(false)}
            onSave={(next) => {
              setAnswers(next);
              setEditing(false);
              // Update the card the owner edited, in place: no message in their
              // name they never typed, no second copy of the card.
              setBubbles((prev) =>
                prev.map((b, idx) => (idx === prev.length - 1 && b.summary ? { ...b, summary: next } : b))
              );
            }}
          />
        </div>
      )}

      {phase === "details" && brand && (
        <div className="pl-[54px]">
          <BrandDetailsCard
            businessName={values().businessName}
            customerId={brand.customerId}
            details={brand.details}
            logoError={brand.logoError}
            onConfirm={confirmDetails}
          />
        </div>
      )}

      {(phase === "creating" || phase === "saving" || phase === "training") && (
        <LiveAssistantTurn avatar={avatar} showHeader={last?.from !== "assistant"} steps={setupSteps} streaming="" />
      )}

      {replies.length > 0 && (
        <div className="flex flex-wrap gap-2 pl-[54px]">
          {replies.map((q) => (
            <button
              key={q.label}
              type="button"
              onClick={q.onClick}
              className={
                q.primary
                  ? "rounded-full bg-brand-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-primary-hover"
                  : "rounded-full border border-theme-border bg-theme-surface px-4 py-2 text-sm text-theme-fg hover:border-theme-fg-muted/40"
              }
            >
              {q.label}
            </button>
          ))}
        </div>
      )}

      {/* The input never goes away; it is only paused while the Brand is created. */}
      <SetupComposer
        onSubmit={(m) => send(m)}
        disabled={busy || !CHAT_PHASES.includes(phase)}
        inputRef={inputRef}
        docked={started}
      />
      <div ref={endRef} />
    </div>
  );
};

export default AssistantSetupChat;
